import { AuthProvider } from 'react-admin';
import { apiClient, LoginResponse, UserResponse, ApiError } from './utils/apiClient';
import { tokenManager } from './utils/tokenManager';
import { API_CONFIG } from './config/api';
import { AvailableLanguage } from './i18n';
import { savePostLoginRedirect } from './utils/postLoginRedirect';

// Cache for user data
let currentUser: UserResponse | null = null;

export const authProvider: AuthProvider = {
    login: async ({ username, password, totpCode, turnstileToken }) => {
        try {
            // Prepare login payload
            const payload: any = { password };
            
            // Backend accepts either email or username
            if (username.includes('@')) {
                payload.email = username;
            } else {
                payload.username = username;
            }
            
            // Add TOTP code if provided (for 2FA)
            if (totpCode) {
                payload.totpCode = totpCode;
            }

            // Cloudflare Turnstile token travels on its conventional header so the
            // backend can verify it without changing the login body schema. The
            // header is only sent when a token is present (Turnstile enabled).
            const requestOptions = turnstileToken
                ? { headers: { 'cf-turnstile-response': turnstileToken } }
                : undefined;

            // Make login request
            const response = await apiClient.post<LoginResponse>(
                API_CONFIG.endpoints.auth.login,
                payload,
                requestOptions
            );

            // Store tokens
            tokenManager.setTokens(response.accessToken, response.refreshToken);
            
            // Cache user data with role and permissions
            currentUser = response.user as UserResponse;
            
            // Log the permissions for debugging
            console.log('Login successful, user permissions:', currentUser.role?.permissions);
            
            // Set language preference if available
            if (response.user?.language) {
                const language = response.user.language as AvailableLanguage;
                try {
                    if (typeof localStorage !== 'undefined' && localStorage) {
                        localStorage.setItem('language', language);
                        // React Admin will pick up the language from localStorage
                        localStorage.setItem('RaStore.locale', language);
                    }
                } catch (storageError) {
                    console.warn('Failed to store language preference during login:', storageError);
                }
            }
            
            return Promise.resolve();
        } catch (error) {
            const apiError = error as ApiError;
            
            // Handle specific error cases
            if (apiError.errorCode === 'AUTH_2FA_REQUIRED' || (apiError.status === 403 && apiError.message.includes('2FA'))) {
                // 2FA required - need to handle this in the UI
                return Promise.reject({
                    message: 'errors.AUTH_2FA_REQUIRED',
                    errorCode: 'AUTH_2FA_REQUIRED',
                    requiresTwoFactor: true,
                });
            }

            if (apiError.errorCode === 'AUTH_ACCOUNT_LOCKED' || apiError.status === 423) {
                // Account locked
                return Promise.reject({
                    message: 'errors.AUTH_ACCOUNT_LOCKED',
                    errorCode: 'AUTH_ACCOUNT_LOCKED',
                });
            }

            // Use error code for translation if available
            const errorCode = apiError.errorCode;
            const message = errorCode
                ? `errors.${errorCode}`
                : apiError.message || 'Login failed';

            return Promise.reject({
                message,
                errorCode,
            });
        }
    },
    
    logout: async () => {
        console.log('[AuthProvider] logout called:', {
            currentUrl: window.location.hash,
            hasRefreshToken: !!tokenManager.getRefreshToken(),
            isRefreshing: tokenManager.isRefreshing(),
            stackTrace: new Error().stack
        });

        // If already on the login page, clear tokens and force reload to unmount
        // stale components that may still be firing API requests
        if (window.location.hash === '#/login') {
            console.log('[AuthProvider] Already on login page, clearing tokens and reloading');
            tokenManager.clearTokens();
            currentUser = null;
            window.location.reload();
            return Promise.resolve();
        }

        // Only perform logout if we actually have tokens
        const refreshToken = tokenManager.getRefreshToken();
        if (refreshToken) {
            console.log('[AuthProvider] Calling backend logout API with refresh token');
            try {
                // Notify backend to invalidate refresh token
                await apiClient.post(API_CONFIG.endpoints.auth.logout, {
                    refreshToken,
                });
                console.log('[AuthProvider] Backend logout successful');
            } catch (error) {
                // Log error but don't prevent logout
                console.error('[AuthProvider] Logout API call failed:', error);
            }
        } else {
            console.log('[AuthProvider] No refresh token, skipping backend logout call');
        }

        // Always clear local tokens and user data
        console.log('[AuthProvider] Clearing local tokens and user data');
        tokenManager.clearTokens();
        currentUser = null;

        // Return a redirect to login page
        return Promise.resolve('/login');
    },
    
    checkError: ({ status, body }: any) => {
        // Only trigger logout on 401 (Unauthorized)
        // 403 (Forbidden) should not trigger logout as it means the user is authenticated but lacks permissions
        if (status === 401) {
            console.log('[AuthProvider] checkError received 401:', {
                isRefreshing: tokenManager.isRefreshing(),
                hasRefreshToken: !!tokenManager.getRefreshToken(),
                isAuthenticated: tokenManager.isAuthenticated()
            });

            // If a refresh is already in progress, don't trigger logout - let it complete
            if (tokenManager.isRefreshing()) {
                console.log('[AuthProvider] Token refresh in progress, waiting...');
                return Promise.resolve();
            }

            // If we reach checkError with a 401, apiClient has already attempted
            // token refresh (and either it failed or the retried request still got 401).
            // Trigger logout to prevent infinite retry loop.
            console.log('[AuthProvider] 401 after apiClient retry — triggering logout');
            savePostLoginRedirect();
            tokenManager.clearTokens();
            currentUser = null;
            return Promise.reject();
        }
        return Promise.resolve();
    },
    
    checkAuth: async () => {
        // Check if we have valid tokens
        if (!tokenManager.isAuthenticated()) {
            savePostLoginRedirect();
            return Promise.reject();
        }

        // If we don't have user data, try to fetch it
        if (!currentUser) {
            try {
                const response = await apiClient.get<{ user: UserResponse }>(
                    API_CONFIG.endpoints.auth.me
                );
                currentUser = response.user;

                // Set language preference if available
                if (currentUser.language) {
                    const language = currentUser.language as AvailableLanguage;
                    try {
                        if (typeof localStorage !== 'undefined' && localStorage) {
                            localStorage.setItem('language', language);
                            localStorage.setItem('RaStore.locale', language);
                        }
                    } catch (storageError) {
                        console.warn('Failed to store language preference:', storageError);
                    }
                }
            } catch (error) {
                // Failed to get user data, clear tokens and reject
                tokenManager.clearTokens();
                currentUser = null;
                savePostLoginRedirect();
                return Promise.reject();
            }
        }

        return Promise.resolve();
    },
    
    getPermissions: async () => {
        // Check if we have user data
        if (!tokenManager.isAuthenticated()) {
            return Promise.resolve([]);
        }

        // Return user's role permissions if available
        if (currentUser?.role?.permissions) {
            return Promise.resolve(currentUser.role.permissions);
        }

        // If no role data, fetch full user details
        try {
            const response = await apiClient.get<{ user: UserResponse }>(
                API_CONFIG.endpoints.auth.me
            );
            currentUser = response.user;
            return Promise.resolve(currentUser.role?.permissions || []);
        } catch (error) {
            console.error('Failed to fetch permissions:', error);
            return Promise.resolve([]);
        }
    },
    
    getIdentity: async () => {
        // Check if we have user data
        if (!tokenManager.isAuthenticated()) {
            return Promise.reject();
        }

        if (currentUser) {
            return Promise.resolve({
                id: currentUser.id,
                fullName: currentUser.username || currentUser.email,
                email: currentUser.email,
                avatar: undefined,
                // vendorIds MUST be on the cached path too. Without this, after
                // checkAuth/getPermissions warm the cache before getIdentity ever
                // fires, the vendor-scoped UI in SiteCreate/SiteEdit silently
                // degrades to the super-admin view.
                vendorIds: currentUser.vendorIds || [],
                roleId: currentUser.roleId,
                roleName: currentUser.role?.name,
            } as any);
        }

        // Try to fetch user data
        try {
            const response = await apiClient.get<{ user: UserResponse }>(
                API_CONFIG.endpoints.auth.me
            );
            currentUser = response.user;
            return Promise.resolve({
                id: currentUser.id,
                fullName: currentUser.username || currentUser.email,
                email: currentUser.email,
                avatar: undefined,
                // vendorIds surfaces vendor membership so vendor-scoped UI
                // (SiteCreate vendor default, etc.) can read it via useGetIdentity().
                vendorIds: currentUser.vendorIds || [],
                roleId: currentUser.roleId,
                roleName: currentUser.role?.name,
            } as any);
        } catch (error) {
            return Promise.reject();
        }
    },
};